import ActivityLoader from '../editor/activity/engine/ActivityLoader';
import ActivityStorageService from '../editor/activity/storage/ActivityStorageService';
import ProgressReporter from '../editor/activity/storage/ProgressReporter';
import ScratchAudio from '../utils/ScratchAudio';
import ActivityProjectService from './ActivityProjectService';
import Lobby from './Lobby';

export default class ActivityHub {
    constructor (container) {
        this._container = container;
        this._curriculum = null;
        this._isPaidUser = Lobby.isUserPaidTier();
        this._progress = {};
        this._summary = null;
        this._element = null;
        this._holdTimer = null;
        this._pressedCard = null;
        this._longPressTriggered = false;
        this._visibleResetCard = null;
        this._resetHideTimer = null;
        this._onGlobalPointerDown = this._handleGlobalPointerDown.bind(this);
    }

    async init () {
        const result = await this._loadCurriculum();
        if (!result.valid) {
            console.error('[ActivityHub] Failed to load curriculum manifest:', result.errors);
            this._renderError(result.errors);
            return;
        }

        this._curriculum = result.data;
        this._progress = await ActivityStorageService.getProgress();
        this._summary = await ProgressReporter.getHubSummary(this._curriculum);

        this._render();
    }

    async _loadCurriculum () {
        const topLevelResult = await ActivityLoader.loadManifest();
        if (!topLevelResult.valid) {
            return topLevelResult;
        }

        const topLevel = topLevelResult.data || {};
        const courseRefs = topLevel.courses || [];
        const resolvedCourses = [];
        const errors = [];

        for (const courseRef of courseRefs) {
            const manifestPath = courseRef.manifestPath
                ? `/activities/${String(courseRef.manifestPath).replace(/^\/+/, '')}`
                : `/activities/courses/${courseRef.courseId}/manifest.json`;
            const courseResult = await ActivityLoader.loadManifest(manifestPath);
            if (!courseResult.valid) {
                errors.push(`${courseRef.courseId}: ${(courseResult.errors || []).join(', ')}`);
                continue;
            }
            resolvedCourses.push({
                ...courseRef,
                ...courseResult.data,
                manifestPath: courseRef.manifestPath
            });
        }

        if (resolvedCourses.length === 0) {
            return {valid: false, errors: errors.length > 0 ? errors : ['No activity courses available']};
        }

        return {
            valid: true,
            data: {
                ...topLevel,
                courses: resolvedCourses
            },
            errors
        };
    }

    _render () {
        if (this._element && this._element.parentNode) {
            this._element.parentNode.removeChild(this._element);
        }

        this._element = document.createElement('div');
        this._element.className = 'activity-hub';

        this._renderSections();

        this._container.appendChild(this._element);
        document.removeEventListener('pointerdown', this._onGlobalPointerDown, true);
        document.addEventListener('pointerdown', this._onGlobalPointerDown, true);
    }

    _renderSections () {
        const continueLessons = this._getContinueLessons();
        const nextLesson = this._summary && this._summary.nextActivity
            ? this._findLesson(this._summary.nextActivity.activityId)
            : null;

        if (continueLessons.length > 0) {
            this._element.appendChild(this._createSectionBar('Continue'));
            this._element.appendChild(this._createContinuePanel(continueLessons));
        } else if (nextLesson) {
            this._element.appendChild(this._createSectionBar('Continue'));
            this._element.appendChild(this._createContinueButtonPanel(nextLesson));
        }

        const excludeIds = new Set(continueLessons.map((ref) => ref.lesson.activityId));
        this._element.appendChild(this._createSectionBar('Activities'));
        this._element.appendChild(this._createActivitiesGrid(excludeIds));
    }

    _createSectionBar (title) {
        const bar = document.createElement('div');
        bar.className = 'activity-hub-section-title';
        bar.textContent = title;
        return bar;
    }

    _createContinuePanel (lessonRefs) {
        const panel = document.createElement('div');
        panel.className = 'activity-hub-continue-panel activity-hub-continue-shelf';
        panel.appendChild(this._createLessonShelf(lessonRefs));
        return panel;
    }

    _createContinueButtonPanel (lessonRef) {
        const panel = document.createElement('div');
        panel.className = 'activity-hub-continue-panel';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'activity-hub-continue-btn';
        button.dataset.activityId = lessonRef.lesson.activityId;
        button.setAttribute('aria-label', `Continue ${lessonRef.lesson.title || lessonRef.lesson.activityId}`);
        button.addEventListener('click', () => {
            this._openLesson(lessonRef.lesson, lessonRef.courseId, lessonRef.unitId);
        });

        const hiddenText = document.createElement('span');
        hiddenText.className = 'activity-hub-continue-text';
        hiddenText.textContent = 'Continue';
        button.appendChild(hiddenText);

        const icon = document.createElement('span');
        icon.className = 'activity-hub-continue-icon';
        icon.setAttribute('aria-hidden', 'true');
        const triangle = document.createElement('span');
        triangle.className = 'activity-hub-continue-triangle';
        icon.appendChild(triangle);
        button.appendChild(icon);

        panel.appendChild(button);
        return panel;
    }

    _createActivitiesGrid (excludeActivityIds = new Set()) {
        const lessons = [];
        for (const course of (this._curriculum.courses || [])) {
            for (const unit of (course.units || [])) {
                for (const lesson of (unit.lessons || [])) {
                    if (excludeActivityIds.has(lesson.activityId)) {
                        continue;
                    }
                    // Gate Activity 4 behind feature flag (hidden unless explicitly enabled)
                    if (lesson.activityId === 'treasure-map' &&
                        !(window.__FF && window.__FF.activity4TreasureMap === true)) {
                        continue;
                    }
                    lessons.push({
                        lesson,
                        courseId: course.courseId,
                        unitId: unit.unitId
                    });
                }
            }
        }

        return this._createLessonShelf(lessons);
    }

    _createLessonShelf (lessonRefs) {
        const grid = document.createElement('div');
        grid.className = 'activity-portfolio-grid';

        const lessons = lessonRefs || [];
        const rows = Math.max(1, Math.ceil(lessons.length / 4));
        grid.dataset.rows = String(rows);
        grid.style.height = `${rows * 31}vh`;

        lessons.forEach(({lesson, courseId, unitId}, index) => {
            const card = this._createActivityCard(lesson, courseId, unitId, index);
            const column = index % 4;
            const row = Math.floor(index / 4);
            card.style.left = `${23.2 * column}%`;
            card.style.top = `${28 * row}vh`;
            grid.appendChild(card);
        });

        return grid;
    }

    _getContinueLessons () {
        const lessonRefs = [];
        for (const course of (this._curriculum.courses || [])) {
            for (const unit of (course.units || [])) {
                for (const lesson of (unit.lessons || [])) {
                    const progress = this._progress[lesson.activityId] || {};
                    const status = this._getLessonStatus(progress);
                    if (status.key !== 'progress') {
                        continue;
                    }
                    lessonRefs.push({
                        lesson,
                        courseId: course.courseId,
                        unitId: unit.unitId,
                        lastOpenedAt: progress.lastOpenedAt || null
                    });
                }
            }
        }

        lessonRefs.sort((a, b) => {
            const aTime = a.lastOpenedAt ? Date.parse(a.lastOpenedAt) : 0;
            const bTime = b.lastOpenedAt ? Date.parse(b.lastOpenedAt) : 0;
            return bTime - aTime;
        });

        return lessonRefs;
    }

    _createActivityCard (lesson, courseId, unitId, tileIndex, opts = {}) {
        const card = document.createElement('div');
        card.className = 'activity-portfolio-card samplethumb';
        card.dataset.activityId = lesson.activityId;
        card.dataset.courseId = courseId;
        card.dataset.unitId = unitId;
        if (!opts.staticCard) {
            card.tabIndex = 0;
            card.setAttribute('role', 'button');
        }

        if (opts.featured) {
            card.classList.add('activity-hub-continue-card');
        }

        const progress = this._progress[lesson.activityId] || {};
        const status = this._getLessonStatus(progress);
        const locked = this._isLessonLocked(lesson);
        const premiumLocked = this._isPremiumLocked(lesson);
        const canReset = status.key !== 'new';
        card.classList.add(`activity-status-${status.key}`);
        if (premiumLocked) {
            card.classList.add('activity-card-premium-locked');
            card.setAttribute('aria-disabled', 'true');
        } else if (locked) {
            card.classList.add('activity-card-locked');
            card.setAttribute('aria-disabled', 'true');
        }
        card.dataset.resettable = canReset ? '1' : '0';
        card.dataset.locked = (locked || premiumLocked) ? '1' : '0';
        card.dataset.premiumLocked = premiumLocked ? '1' : '0';

        const thumb = document.createElement('div');
        thumb.className = `activity-card-thumb thumb pos${tileIndex % 8}`;

        const frame = document.createElement('div');
        frame.className = 'activity-card-woodframe woodframe';
        thumb.appendChild(frame);

        const preview = document.createElement('div');
        preview.className = 'activity-card-preview sampleicon';
        preview.style.backgroundImage = lesson.previewImage ? `url('${lesson.previewImage}')` : 'none';

        if (lesson.previewSprite) {
            const sprite = document.createElement('div');
            sprite.className = 'activity-card-sprite';
            sprite.style.backgroundImage = `url('${lesson.previewSprite}')`;
            preview.appendChild(sprite);
        }

        thumb.appendChild(preview);

        if (premiumLocked) {
            const lockBadge = document.createElement('div');
            lockBadge.className = 'activity-card-lock-badge';
            lockBadge.setAttribute('aria-label', 'Premium');
            lockBadge.textContent = '\uD83D\uDD12';
            thumb.appendChild(lockBadge);
        }

        card.appendChild(thumb);

        const title = document.createElement('p');
        title.className = 'activity-card-title';
        title.textContent = lesson.title || lesson.activityId;
        card.appendChild(title);

        if (!opts.hideStars) {
            const earned = progress.stars || 0;
            if (earned > 0) {
                const stars = document.createElement('div');
                stars.className = 'activity-card-stars';
                for (let i = 1; i <= 3; i++) {
                    const star = document.createElement('span');
                    star.className = i <= earned ? 'activity-card-star earned' : 'activity-card-star empty';
                    star.textContent = i <= earned ? '\u2605' : '\u2606';
                    stars.appendChild(star);
                }
                card.appendChild(stars);
            }
        }

        if (canReset && !opts.staticCard) {
            const resetButton = document.createElement('button');
            resetButton.className = 'activity-card-reset-btn';
            resetButton.type = 'button';
            resetButton.textContent = 'Reset';
            resetButton.setAttribute('aria-label', `Reset ${lesson.title || lesson.activityId}`);
            resetButton.addEventListener('pointerdown', (event) => {
                event.stopPropagation();
            });
            resetButton.addEventListener('pointerup', async (event) => {
                event.preventDefault();
                event.stopPropagation();
                await this._resetLesson(lesson, resetButton);
            });
            thumb.appendChild(resetButton);
        }

        if (!opts.staticCard) {
            card.addEventListener('pointerdown', (event) => {
                this._handleCardPointerDown(event, card, canReset);
            });
            card.addEventListener('pointerup', (event) => {
                this._handleCardPointerUp(event, lesson, courseId, unitId);
            });
            card.addEventListener('pointerleave', () => this._clearHoldTimer());
            card.addEventListener('pointercancel', () => this._clearHoldTimer());
            card.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (premiumLocked) {
                        this._showPremiumUpsell();
                    } else if (!locked) {
                        this._openLesson(lesson, courseId, unitId);
                    }
                }
            });
        }

        return card;
    }

    _handleCardPointerDown (event, card, canReset) {
        if (!card || !canReset || (event && event.button && event.button !== 0)) {
            return;
        }

        if (event && event.target && event.target.closest && event.target.closest('.activity-card-reset-btn')) {
            return;
        }

        this._clearHoldTimer();
        this._pressedCard = card;
        this._longPressTriggered = false;
        this._holdTimer = setTimeout(() => {
            this._longPressTriggered = true;
            this._showResetButton(card);
            try {
                ScratchAudio.sndFX('keydown.wav');
            } catch (_) {}
        }, 550);
    }

    _handleCardPointerUp (event, lesson, courseId, unitId) {
        const wasLongPress = this._longPressTriggered;
        this._clearHoldTimer();

        if (event && event.target && event.target.closest && event.target.closest('.activity-card-reset-btn')) {
            return;
        }

        if (wasLongPress) {
            this._longPressTriggered = false;
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            return;
        }

        if (this._isPremiumLocked(lesson)) {
            try {
                ScratchAudio.sndFX('cut.wav');
            } catch (_) {}
            this._showPremiumUpsell();
            return;
        }

        if (this._isLessonLocked(lesson)) {
            try {
                ScratchAudio.sndFX('cut.wav');
            } catch (_) {}
            return;
        }

        this._openLesson(lesson, courseId, unitId);
    }

    _clearHoldTimer () {
        if (this._holdTimer) {
            clearTimeout(this._holdTimer);
            this._holdTimer = null;
        }
        this._pressedCard = null;
    }

    _showResetButton (card) {
        if (!card || !this._element) {
            return;
        }

        this._hideResetButton();
        card.classList.add('activity-reset-visible');
        this._visibleResetCard = card;

        if (this._resetHideTimer) {
            clearTimeout(this._resetHideTimer);
        }
        this._resetHideTimer = setTimeout(() => {
            this._hideResetButton();
        }, 4500);
    }

    _hideResetButton () {
        if (this._resetHideTimer) {
            clearTimeout(this._resetHideTimer);
            this._resetHideTimer = null;
        }
        if (this._visibleResetCard) {
            this._visibleResetCard.classList.remove('activity-reset-visible');
            this._visibleResetCard = null;
        }
    }

    _handleGlobalPointerDown (event) {
        if (!this._visibleResetCard) {
            return;
        }

        const target = event && event.target;
        if (!target || !this._visibleResetCard.contains(target)) {
            this._hideResetButton();
        }
    }

    async _resetLesson (lesson, resetButton) {
        if (!lesson || !lesson.activityId) {
            return;
        }

        if (resetButton) {
            resetButton.disabled = true;
        }

        try {
            ScratchAudio.sndFX('cut.wav');
        } catch (_) {}

        try {
            await ActivityProjectService.resetLesson(lesson.activityId);
            this._progress = await ActivityStorageService.getProgress();
            this._summary = await ProgressReporter.getHubSummary(this._curriculum);
            this._hideResetButton();
            this._render();
        } catch (err) {
            console.error('[ActivityHub] Failed to reset lesson:', err);
            if (resetButton) {
                resetButton.disabled = false;
            }
        }
    }

    _getLessonStatus (progress) {
        if (progress.status === 'completed') {
            return {key: 'complete'};
        }
        if (progress.status === 'in_progress' || progress.projectId || progress.lastOpenedAt) {
            return {key: 'progress'};
        }
        return {key: 'new'};
    }

    _isLessonLocked (lesson) {
        return ProgressReporter._isLessonLocked(lesson, this._progress);
    }

    _isPremiumLocked (lesson) {
        if (!lesson || !lesson.tier || lesson.tier === 'free') {
            return false;
        }
        return !this._isPaidUser;
    }

    _showPremiumUpsell () {
        if (typeof window.SubscriptionManager !== 'undefined' &&
            typeof window.SubscriptionManager.showUpsellModal === 'function') {
            window.SubscriptionManager.showUpsellModal(document.body, {
                onSubscribe: (plan) => {
                    if (typeof window.SubscriptionManager.startCheckout === 'function') {
                        return window.SubscriptionManager.startCheckout(plan);
                    }
                    return Promise.resolve();
                },
                onDismiss: () => {}
            });
        }
    }

    _findLesson (activityId) {
        for (const course of (this._curriculum.courses || [])) {
            for (const unit of (course.units || [])) {
                for (const lesson of (unit.lessons || [])) {
                    if (lesson.activityId === activityId) {
                        return {
                            lesson,
                            courseId: course.courseId,
                            unitId: unit.unitId
                        };
                    }
                }
            }
        }
        return null;
    }

    async _openLesson (lesson, courseId, unitId) {
        try {
            ScratchAudio.sndFX('tap.wav');
        } catch (_) {}

        try {
            await ActivityProjectService.openLesson(lesson, courseId, unitId);
        } catch (err) {
            console.error('[ActivityHub] Failed to open lesson:', err);
            this._renderError([err.message || 'Unable to open activity']);
        }
    }

    _extractLessons (course) {
        const lessons = [];
        for (const unit of (course.units || [])) {
            for (const lesson of (unit.lessons || [])) {
                lessons.push(lesson);
            }
        }
        return lessons;
    }

    _getTotalLessons () {
        return (this._curriculum.courses || []).reduce((sum, course) => sum + this._extractLessons(course).length, 0);
    }

    _getCompletedLessons () {
        return Object.values(this._progress).filter((entry) => entry && entry.status === 'completed').length;
    }

    _renderError (errors) {
        if (this._element && this._element.parentNode) {
            this._element.parentNode.removeChild(this._element);
            this._element = null;
        }

        const wrapper = document.createElement('div');
        wrapper.className = 'activity-hub-error';

        const title = document.createElement('p');
        title.textContent = 'We could not load activities right now.';
        wrapper.appendChild(title);

        if (Array.isArray(errors) && errors.length > 0) {
            const detail = document.createElement('p');
            detail.className = 'activity-hub-error-detail';
            detail.textContent = errors[0];
            wrapper.appendChild(detail);
        }

        const retry = document.createElement('button');
        retry.className = 'activity-hub-retry-btn';
        retry.textContent = 'Try Again';
        retry.addEventListener('pointerup', () => window.location.reload());
        wrapper.appendChild(retry);

        this._container.appendChild(wrapper);
    }

    destroy () {
        this._clearHoldTimer();
        this._hideResetButton();
        document.removeEventListener('pointerdown', this._onGlobalPointerDown, true);
        if (this._element && this._element.parentNode) {
            this._element.parentNode.removeChild(this._element);
        }
        this._element = null;
    }
}
